import { useEffect, useRef, useState } from 'react';

const cases = [
  {
    icon: 'ri-file-text-line',
    tag: '婚姻调查',
    title: '婚外情调查成功案例',
    desc: '委托人怀疑配偶有婚外情，我们通过专业调查获取了确凿证据，帮助委托人理性决策。',
    result: '成功收集有效证据，案件圆满解决',
  },
  {
    icon: 'ri-user-search-line',
    tag: '寻人找人',
    title: '定位找人成功案例',
    desc: '一位父亲与子女失联多年，我们通过专业手段在全国范围内寻找，最终成功找到了对方的下落。',
    result: '成功找到目标人员，促成亲人团聚',
  },
  {
    icon: 'ri-building-2-line',
    tag: '商业调查',
    title: '商业调查成功案例',
    desc: '某企业准备与另一公司合作，委托我们进行背景调查，发现了对方存在的严重风险隐患。',
    result: '成功发现潜在风险，避免重大损失',
  },
  {
    icon: 'ri-bank-line',
    tag: '资产调查',
    title: '资产调查成功案例',
    desc: '委托人在民事诉讼胜诉后，对方声称无力偿还，我们调查发现了对方的隐匿资产。',
    result: '成功找到隐匿资产，保障合法权益',
  },
  {
    icon: 'ri-shield-user-line',
    tag: '背景调查',
    title: '背景调查成功案例',
    desc: '客户准备与某人结婚前委托调查对方背景，经调查发现对方已有家庭且存在多起诈骗前科。',
    result: '及时揭露真相，避免客户遭受感情和财产损失',
  },
  {
    icon: 'ri-eye-line',
    tag: '行为监控',
    title: '行为监控成功案例',
    desc: '某公司怀疑高管泄露商业机密，通过合法监控确认该高管与竞争对手秘密会面并交换文件。',
    result: '获取关键证据，帮助企业及时处理内部风险',
  },
];

const CasesSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="cases" className="py-20 bg-[#0a1209] relative overflow-hidden">
      {/* Glow blobs */}
      <div className="absolute top-1/2 right-0 w-80 h-80 bg-[#c9a84c]/4 rounded-full blur-3xl pointer-events-none -translate-y-1/2"></div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="w-8 h-px bg-gradient-to-r from-transparent to-[#c9a84c]"></span>
            <span className="text-[#c9a84c] text-xs font-semibold tracking-widest uppercase">Success Cases</span>
            <span className="w-8 h-px bg-gradient-to-l from-transparent to-[#c9a84c]"></span>
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-white mb-2">成功案例</h2>
          <p className="text-white/35 text-sm">我们已成功解决众多复杂调查案件</p>
        </div>

        <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cases.map((c, idx) => (
            <div
              key={c.title}
              className="relative bg-[#162218] rounded-xl p-5 border border-[#c9a84c]/10 hover:border-[#c9a84c]/55 transition-all duration-500 group overflow-hidden"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(28px)',
                transition: `opacity 0.55s ease ${idx * 0.08}s, transform 0.55s ease ${idx * 0.08}s, border-color 0.3s ease`,
              }}
            >
              {/* Shine sweep */}
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-[#c9a84c]/7 to-transparent skew-x-12 pointer-events-none"></span>

              {/* Top glow line */}
              <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/0 to-transparent group-hover:via-[#c9a84c]/70 transition-all duration-500 pointer-events-none"></span>

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#c9a84c]/10 rounded-lg group-hover:bg-[#c9a84c]/22 transition-colors duration-300">
                    <i className={`${c.icon} text-xl text-[#c9a84c]`}></i>
                  </div>
                  <span className="text-xs text-[#c9a84c]/70 border border-[#c9a84c]/20 rounded-full px-2 py-0.5 group-hover:border-[#c9a84c]/50 transition-colors duration-300">
                    {c.tag}
                  </span>
                </div>
                <h3 className="text-sm font-bold text-white mb-2 group-hover:text-[#c9a84c] transition-colors duration-300">{c.title}</h3>
                <p className="text-xs text-white/45 leading-relaxed mb-3">{c.desc}</p>
                <div className="flex items-start gap-2 bg-[#c9a84c]/6 border border-[#c9a84c]/20 rounded-lg px-3 py-2">
                  <i className="ri-check-double-line text-[#c9a84c] text-sm flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-[#c9a84c]/90 font-medium leading-relaxed">{c.result}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CasesSection;
